import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Card, CardActions, CardContent, Container, Grid, IconButton, InputAdornment, InputLabel, TextField, Typography } from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff, AlternateEmail, CheckCircle } from '@material-ui/icons';
import { Link, useLocation } from 'react-router-dom';
import { getErrorMessage } from 'src/utils/getAxiosErrorMessage';
import { useAppData, useLoading } from '../../providers';
import { useStyles } from './styles';
import { RepresentativeSignUpForm } from './RepresentativeSignUpModel';
import { extractLocaleFromPath, getTranslation, getURLParam, languages, PATH_LOGIN_AS_REPRESENTATIVE, supportedLocales } from '../../utils';
import { resetPassword } from './RepresentativeSignUpService';
import { Footer, UnauthorizedHeader } from '../../components';
import { getTranslationsByBrokerId } from '../Login/LoginService';
import { trackAmplitudeData } from '../../services/amplitudeService';

export const RepresentativeSignUp: FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { isLoading, setIsLoading } = useLoading();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const { logoUrl, footerLogoUrl, saveTranslations, saveLocale } = useAppData();
  const [logo, setLogo] = useState<string>('');
  const [footerLogo, setFooterLogo] = useState<string>('');
  const [translation, setTranslation] = useState<any>([]);
  const [selectedLocale, setSelectedLocale] = useState<any>(languages[0]);
  const location = useLocation();
  const lang = extractLocaleFromPath(location.pathname);
  const [successedSignUp, setSuccessedSignUp] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required('Required')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})/, 'Incorrect'),
    confirmPassword: Yup.string()
      .required('Required')
      .oneOf([Yup.ref('password'), null], 'Not match'),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RepresentativeSignUpForm>({
    resolver: yupResolver(validationSchema),
  });
  const { ref: passwordHookRef, ...passwordHookRefProps } = register('password');
  const { ref: confirmPasswordHookRef, ...confirmPasswordHookRefProps } = register('confirmPassword');

  const email = getURLParam('Email');
  const resetToken = getURLParam('Token');
  const brokerId = getURLParam('BrokerId');

  const getTranslationList = useCallback(
    async (broker: string) => {
      const translations = await getTranslationsByBrokerId(broker);
      saveTranslations(JSON.stringify(translations.data));
      setTranslation(translations.data);
    },
    [saveTranslations],
  );

  const handleLocaleChange = useCallback(
    async (event: React.ChangeEvent<{ value: unknown }>) => {
      const selectedLanguage = languages.find((item) => item.value === (event.target.value as string)) || languages[0];
      setSelectedLocale(selectedLanguage);
      saveLocale(selectedLanguage.value);
      await getTranslationList(brokerId);
    },
    [getTranslationList, brokerId, saveLocale],
  );

  useEffect(() => {
    if (brokerId) {
      (async () => {
        if (lang && supportedLocales.includes(lang)) {
          const selectedLanguage = languages.find((item) => item.value === lang) || languages[0];
          setSelectedLocale(selectedLanguage);
          saveLocale(lang);
          if (selectedLanguage.value !== languages[0].value) {
            trackAmplitudeData('partnerPortal_action_languageChanged', {
              page_language: selectedLanguage.value,
            });
          }
          await getTranslationList(brokerId);
        } else {
          await getTranslationList(brokerId);
        }
      })();
    }
  }, [getTranslationList, brokerId, lang, saveLocale]);

  useEffect(() => {
    setLogo(logoUrl);
    setFooterLogo(footerLogoUrl);
  }, [logoUrl, footerLogoUrl]);

  const signUp = useCallback(
    async (data: RepresentativeSignUpForm) => {
      setIsLoading(true);
  
      try {
        await resetPassword({
          email,
          password: data.password,
          resetToken,
          brokerId: Number(brokerId),
        });
  
        setSuccessedSignUp(true);
      } catch (error: unknown) {
        const errorMessage = getErrorMessage(error);
  
        enqueueSnackbar(getTranslation(translation, errorMessage), {
          variant: 'error',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, email, resetToken, enqueueSnackbar, translation, brokerId],
  );

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Grid container direction="column" className={classes.root}>
      <Grid item>
        <UnauthorizedHeader logo={logo} selectedLocale={selectedLocale} handleLocaleChange={handleLocaleChange} />
      </Grid>
      <Grid item className={classes.margin}>
        {successedSignUp ? (
          <Container className={classes.container}>
            <Card className={classes.successCard}>
              <CardContent>
                <CheckCircle className={classes.checkIcon} />
                <Typography className={classes.title}>{translation.password_set}</Typography>
                <p>{translation.login_to_your_representative_account}</p>
              </CardContent>
              <CardActions className={classes.successAction}>
                <Button variant="contained" color="primary" className={classes.submitButton} component={Link} to={PATH_LOGIN_AS_REPRESENTATIVE}>
                  {translation.go_to_login_page}
                </Button>
              </CardActions>
            </Card>
          </Container>
        ) : (
          <Container className={classes.container}>
            <form onSubmit={handleSubmit(signUp)}>
              <Card className={classes.card}>
                <CardContent>
                  <Typography className={classes.title}>{translation.text_1370}</Typography>
                  <div className={classes.padding}>
                    <InputLabel className={classes.label}>
                      {translation.text_1371}
                      <span className={classes.required}>*</span>
                    </InputLabel>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="email"
                      type="email"
                      margin="dense"
                      variant="outlined"
                      className={classes.disabledInput}
                      value={email}
                      placeholder={translation.text_1376}
                      disabled
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <AlternateEmail className={classes.disabledIcon} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <InputLabel className={`${classes.padding} ${classes.label}`}>
                      {translation.text_1372}
                      <span className={classes.required}>*</span>
                    </InputLabel>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="password"
                      margin="dense"
                      variant="outlined"
                      className={classes.input}
                      placeholder={translation.text_1377}
                      type={showPassword ? 'text' : 'password'}
                      error={!!errors.password}
                      helperText={errors?.password?.message && (errors?.password?.message === 'Required' ? translation.text_1366 : translation.text_1367)}
                      inputRef={passwordHookRef}
                      {...passwordHookRefProps}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword}>
                              {showPassword ? <Visibility className={classes.icon} /> : <VisibilityOff className={classes.icon} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <InputLabel className={`${classes.padding} ${classes.label}`}>
                      {translation.text_1373}
                      <span className={classes.required}>*</span>
                    </InputLabel>
                    <TextField
                      autoComplete="off"
                      fullWidth
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      margin="dense"
                      variant="outlined"
                      className={classes.input}
                      placeholder={translation.text_1374}
                      error={!!errors.confirmPassword}
                      helperText={errors?.confirmPassword?.message && (errors?.confirmPassword?.message === 'Required' ? translation.text_1368 : translation.text_1369)}
                      inputRef={confirmPasswordHookRef}
                      {...confirmPasswordHookRefProps}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={handleClickShowConfirmPassword} onMouseDown={handleMouseDownPassword}>
                              {showConfirmPassword ? <Visibility className={classes.icon} /> : <VisibilityOff className={classes.icon} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </CardContent>
                <CardActions className={classes.cardActions}>
                  <Button type="submit" variant="contained" color="primary" className={classes.submitButton} fullWidth disabled={isLoading}>
                    {translation.text_1375}
                  </Button>
                </CardActions>
              </Card>
            </form>
          </Container>
        )}
      </Grid>
      <Grid item>
        <Footer translation={translation} logo={footerLogo} />
      </Grid>
    </Grid>
  );
};
